import {
  buildRedlineSegments,
  type RedlineSegmentKind,
} from '@/services/playbooks/playbookRunDisplay';
import type { WordSuggestion } from '@/types/playbookWordAddin';

/**
 * A redline segment as the task pane renders it. `added-extension` is the pane's
 * own kind for text that was appended rather than swapped in: with nothing removed
 * alongside it there is no strikethrough to read it against, so it gets a highlight.
 */
export type PreviewSegment = {
  kind: RedlineSegmentKind | 'added-extension';
  text: string;
};

/** The inline before/after preview shown in a suggestion card's body. */
export function suggestionPreviewSegments(suggestion: WordSuggestion): PreviewSegment[] {
  if (suggestion.result_type === 'extraction') {
    // Classification cards show their matched class in the pill, not the body.
    if (suggestion.matched_option_names) {
      return [];
    }
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    return [{ kind: 'unchanged', text: suggestion.extraction_answer || 'No value extracted' }];
  }

  const affectedText = suggestion.text_to_replace ?? '';
  const proposedText = suggestion.proposed_text ?? '';

  if (affectedText.trim() && proposedText.trim()) {
    const segments = buildRedlineSegments(affectedText, proposedText);
    const isPureInsertion = !segments.some(segment => segment.kind === 'removed');
    return segments.map(segment =>
      segment.kind === 'added' && isPureInsertion
        ? { kind: 'added-extension' as const, text: segment.text }
        : segment
    );
  }

  if (proposedText.trim()) {
    return [{ kind: 'added', text: proposedText }];
  }

  if (affectedText.trim()) {
    return [{ kind: 'removed', text: affectedText }];
  }

  return [{ kind: 'unchanged', text: suggestion.explanation }];
}

/** Whether the Word layer has anything to search for when locating this suggestion. */
export function canFocusSuggestion(suggestion: WordSuggestion): boolean {
  return Boolean(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    suggestion.text_to_replace?.trim() || suggestion.extraction_highlighted_text?.length
  );
}

/** Extraction cards and matched classifications carry no redline to apply or decline. */
export function isActionableSuggestion(suggestion: WordSuggestion): boolean {
  return suggestion.result_type !== 'extraction';
}
